import React, { useEffect, useState } from 'react';
import { Container, Box, Typography } from '@mui/material';
import ApiService from '../../services/api';

const PrivacyPolicy = () => {
  const lastUpdated = 'November 2025';
  const [systemSettings, setSystemSettings] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const settings = await ApiService.getSystemSettings();
        if (!mounted) return;
        setSystemSettings(settings || null);
        try { localStorage.setItem('systemSettings', JSON.stringify(settings)); } catch (_) {}
        try { localStorage.setItem('adminSettings', JSON.stringify(settings)); } catch (_) {}
      } catch (_) {
        try {
          const raw = localStorage.getItem('systemSettings') || localStorage.getItem('adminSettings');
          const parsed = raw ? JSON.parse(raw) : null;
          if (mounted) setSystemSettings(parsed);
        } catch (_) {}
      }
    })();
    // Listen for admin settings updates to refresh content live
    const onSettingsUpdated = (e) => {
      const detail = e?.detail || null;
      if (detail && mounted) {
        setSystemSettings(detail);
      } else {
        // Fallback: try localStorage if available
        try {
          const raw = localStorage.getItem('adminSettings');
          if (raw && mounted) setSystemSettings(JSON.parse(raw));
        } catch (_) {}
      }
    };
    try { window.addEventListener('systemSettingsUpdated', onSettingsUpdated); } catch (_) {}
    return () => { mounted = false; };
  }, []);

  const adminPrivacy = (systemSettings?.legal?.privacyPolicy || '').trim();

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box>
        <Typography variant="h3" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Last updated: {lastUpdated}
        </Typography>
        {adminPrivacy ? (
          <Typography paragraph sx={{ whiteSpace: 'pre-wrap' }}>
            {adminPrivacy}
          </Typography>
        ) : (
          <>
        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Overview
        </Typography>
        <Typography paragraph>
          This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use the Job Portal platform, including web and mobile experiences. We respect your privacy and are committed to protecting personal data. We do not sell personal information. We only share limited data with employers when you explicitly request or consent, or when it is necessary to deliver a feature you choose to use (for example, paid contact requests).
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Information We Collect
        </Typography>
        <Typography paragraph>
          We collect information you provide directly and data collected automatically:
        </Typography>
        <ul>
          <li>
            <Typography component="span" paragraph>
              Account data: name, email, phone, role (job seeker or employer), profile details, work history, education, skills, company information.
            </Typography>
          </li>
          <li>
            <Typography component="span" paragraph>
              Content: resumes/CVs, job postings, messages, reviews, uploads and documents you submit.
            </Typography>
          </li>
          <li>
            <Typography component="span" paragraph>
              Transaction data: purchases, payment status, invoicing details (processed via third‑party payment providers; we never store full card numbers).
            </Typography>
          </li>
          <li>
            <Typography component="span" paragraph>
              Usage data: device information, browser type, IP address, pages visited, referring URLs, and interaction events for security and analytics.
            </Typography>
          </li>
        </ul>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          How We Use Information
        </Typography>
        <Typography paragraph>
          We process data for the following purposes:
        </Typography>
        <ul>
          <li>
            <Typography component="span" paragraph>
              Provide and improve the platform, including profile creation, job search, posting, messaging, and support.
            </Typography>
          </li>
          <li>
            <Typography component="span" paragraph>
              Facilitate employer‑job seeker interactions you choose, such as paid contact requests. When enabled, we disclose only the minimum necessary contact information to the paying party and only after consent or clear initiation by the job seeker.
            </Typography>
          </li>
          <li>
            <Typography component="span" paragraph>
              Prevent fraud, enforce policies, secure accounts, and detect abuse, including anti‑scraping and rate‑limit controls.
            </Typography>
          </li>
          <li>
            <Typography component="span" paragraph>
              Communicate with you about your account, transactions, service updates, and promotional messages (you may opt out of marketing communications).
            </Typography>
          </li>
          <li>
            <Typography component="span" paragraph>
              Comply with legal obligations and respond to lawful requests from authorities.
            </Typography>
          </li>
        </ul>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Legal Bases (where applicable)
        </Typography>
        <Typography paragraph>
          We rely on consent (e.g., marketing), contract (e.g., providing the platform and paid features you request), legitimate interests (e.g., platform security and improvements), and compliance with law.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Data Sharing and Third Parties
        </Typography>
        <Typography paragraph>
          We do not sell your personal data. We may share data with:
        </Typography>
        <ul>
          <li>
            <Typography component="span" paragraph>
              Employers when you engage with their listings or explicitly authorize contact; disclosure is limited to what is necessary (e.g., name and contact details).
            </Typography>
          </li>
          <li>
            <Typography component="span" paragraph>
              Service providers acting on our behalf (e.g., payments, cloud hosting, analytics, email delivery), bound by contractual confidentiality and data protection obligations.
            </Typography>
          </li>
          <li>
            <Typography component="span" paragraph>
              Authorities where required by law, to protect rights, safety, and prevent fraud.
            </Typography>
          </li>
        </ul>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Paid Contact Feature
        </Typography>
        <Typography paragraph>
          Employers may purchase access to contact a job seeker. We only disclose contact details when: (a) the job seeker has consented, or (b) the job seeker has initiated or responded to an employer interaction indicating willingness to be contacted, or (c) disclosure is necessary to fulfill a service the job seeker selects. Unauthorised sharing, scraping, resale, or marketing use of contact details is strictly prohibited and may lead to suspension and legal action.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Your Rights
        </Typography>
        <Typography paragraph>
          Subject to applicable law, you may request access, correction, deletion, restriction, objection to processing, and data portability. You may withdraw consent at any time where processing relies on consent. Contact us using the details below to exercise your rights.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Data Retention
        </Typography>
        <Typography paragraph>
          We retain personal data while your account is active and as necessary to provide the service, comply with legal obligations, resolve disputes, and enforce agreements. We may anonymize data for analytics.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Security
        </Typography>
        <Typography paragraph>
          We implement administrative, technical, and physical safeguards to protect data. No method of transmission or storage is 100% secure; we continuously improve our security measures.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          International Transfers
        </Typography>
        <Typography paragraph>
          Your information may be transferred to and processed in countries other than your own. We take steps to ensure appropriate protections consistent with applicable law.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Children’s Privacy
        </Typography>
        <Typography paragraph>
          The platform is not directed to children under 16. We do not knowingly collect personal data from children.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Changes to This Policy
        </Typography>
        <Typography paragraph>
          We may update this Privacy Policy from time to time. Material changes will be communicated by posting an updated version on this page and updating the “Last updated” date.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Contact Us
        </Typography>
        <Typography paragraph>
          If you have questions or requests, contact us at: support@jobitzm.com or jobitzm@gmail.com.
        </Typography>
          </>
        )}
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;